import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useCheckoutState } from '../../utils/checkoutState';
import { config } from '../../utils/config';
import { getFormat } from '../../utils/getValidator';
import { mountScript } from '../../utils/useScript';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    wpwlOptions?: { locale?: string; onReady: () => void; onBeforeSubmitCard: () => void };
  }
}

// https://arvato.docs.oppwa.com/tutorials/integration-guide

const ArvatoPayment = () => {
  const { checkoutState } = useCheckoutState();
  const initializationData = checkoutState.orderConfirmation?.initializationData;
  const action = checkoutState.orderConfirmation?.redirect?.url;
  const dataBrands = initializationData?.paymentBrand.join(' ');
  const checkoutId = initializationData?.sessionId;
  const isRecurring = checkoutState.orderState.payment?.selected?.[0].configuration?.isRecurring;
  const { formatMessage } = useIntl();

  const getRecurringCheckbox = () => {
    const checkboxContainer = document.createElement('div');
    checkboxContainer.setAttribute('style', 'display:flex');
    checkboxContainer.textContent = formatMessage({ id: 'payment.paymentMethod.creditCardForm.storeDetails' });

    const checkbox = document.createElement('input');
    checkbox.setAttribute('type', 'checkbox');
    checkbox.setAttribute('name', 'createRegistration');
    checkbox.classList.add('customInput');

    checkboxContainer.appendChild(checkbox);
    return checkboxContainer;
  };

  const validateCardHolderName = () => {
    const holderField: HTMLInputElement | null = document.querySelector('.wpwl-control-cardHolder');
    const holderValue = holderField?.value.trim() ?? '';
    const hasInvalidCharacters = !getFormat('name').test(holderValue);

    if (holderField && (holderValue.length < 2 || hasInvalidCharacters)) {
      holderField.classList.add('wpwl-has-error');
      const hint = document.createElement('div');
      hint.classList.add('wpwl-hint', 'wpwl-hint-cardHolderError');
      hint.textContent = formatMessage({ id: 'payment.paymentMethod.creditCardForm.error.invalidCardHolderName' });
      holderField.insertAdjacentElement('afterend', hint);
      return false;
    }
    return true;
  };

  useEffect(() => {
    window.wpwlOptions = {
      locale: config.locale.substring(0, 2),
      onReady: () => {
        const buttonPay = document.querySelector('.wpwl-form-card .wpwl-button-pay');
        if (isRecurring) {
          buttonPay?.insertAdjacentElement('beforebegin', getRecurringCheckbox());
        }
        buttonPay?.addEventListener('click', () => {
          validateCardHolderName();
        });
      },
      onBeforeSubmitCard: validateCardHolderName
    };

    if (IS_WEB_COMPONENT) {
      const arvatoPaymentContainer = document.getElementById('arvato-payment-container');
      const form = document.createElement('form');
      form.action = action!;
      form.className = 'paymentWidgets';
      form.setAttribute('data-brands', dataBrands);
      arvatoPaymentContainer?.appendChild(form);
    }

    const arvatoSdkHost = config.isProduction ? 'oppwa.com' : 'test.oppwa.com';
    mountScript(`https://${arvatoSdkHost}/v1/paymentWidgets.js?checkoutId=${checkoutId}`);

    return () => {
      delete window.wpwlOptions;

      if (IS_WEB_COMPONENT && !import.meta.env.DEV) {
        const arvatoPaymentContainer = document.getElementById('arvato-payment-container');
        if (arvatoPaymentContainer) {
          arvatoPaymentContainer.innerHTML = '';
        }
      }
    };
  }, []);

  return IS_WEB_COMPONENT && !import.meta.env.DEV ?
      <slot name="arvato-payment-container" />
    : <form action={action} className="paymentWidgets" data-brands={dataBrands} />;
};

export default ArvatoPayment;
